/* eslint-disable react-hooks/refs -- deliberate "ref-guarded render-time setState" pattern (see each call site); permitted by React but not understood by react-hooks/refs. */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Paperclip, Maximize2, Minimize2 } from 'lucide-react';
import { SubjectField } from '../SubjectField';
import { useAutoSave } from './use-auto-save';
import { ChatInputActions } from './ChatInputActions';

interface ChatInputProps {
  onSend: (content: string) => void | Promise<void>;
  onSaveDraft?: (content: string, draftId?: string) => string | Promise<string>;
  onChange?: (content: string) => void;
  onAttach?: () => void;
  placeholder?: string;
  disabled?: boolean;
  isSending?: boolean;
  editingDraftContent?: string;
  onUpdateDraft?: (content: string) => void;
  onCancelEdit?: () => void;
  subject?: string;
  onSubjectChange?: (subject: string) => void;
  autoFocus?: boolean;
  initialValue?: string;
}

export function ChatInput({
  onSend,
  onSaveDraft,
  onChange,
  onAttach,
  placeholder = 'Type a message...',
  disabled = false,
  isSending = false,
  editingDraftContent,
  onUpdateDraft,
  onCancelEdit,
  subject = '',
  onSubjectChange,
  autoFocus = false,
  initialValue = ''
}: ChatInputProps) {
  const [message, setMessage] = useState(initialValue);
  const [isExpanded, setIsExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevEditingDraftRef = useRef(false);
  const isEditingDraft =
    editingDraftContent !== undefined && editingDraftContent !== '';

  const { isAutoSaving, triggerAutoSave, clearAutoSave } = useAutoSave({
    onSaveDraft,
    isEditingDraft
  });

  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const minHeight = 80;
      const maxHeight = isExpanded ? 400 : 150;
      const targetHeight = Math.max(
        minHeight,
        Math.min(scrollHeight, maxHeight)
      );
      textareaRef.current.style.height = `${targetHeight}px`;
    }
  }, [isExpanded]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [isExpanded, adjustTextareaHeight]);

  // Sync initialValue into message when it arrives asynchronously (render-phase).
  const [prevInitialValue, setPrevInitialValue] = useState(initialValue);
  if (initialValue !== prevInitialValue) {
    setPrevInitialValue(initialValue);
    if (initialValue && !prevInitialValue) {
      setMessage(initialValue);
    }
  }
  useEffect(() => {
    if (initialValue && initialValue !== prevInitialValue) {
      const id = setTimeout(adjustTextareaHeight, 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [initialValue, prevInitialValue, adjustTextareaHeight]);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  // Load draft content when editing (render-phase prop sync for setState; side
  // effects like focus/height adjustment stay in an effect).
  const [prevIsEditingDraft, setPrevIsEditingDraft] = useState(isEditingDraft);
  const [prevEditingDraftContent, setPrevEditingDraftContent] =
    useState(editingDraftContent);
  if (
    isEditingDraft !== prevIsEditingDraft ||
    editingDraftContent !== prevEditingDraftContent
  ) {
    setPrevIsEditingDraft(isEditingDraft);
    setPrevEditingDraftContent(editingDraftContent);
    if (isEditingDraft && editingDraftContent) {
      setMessage(editingDraftContent);
      if (!prevEditingDraftRef.current) {
        setIsExpanded(true);
      }
    } else if (!isEditingDraft && prevEditingDraftRef.current) {
      setMessage('');
    }
  }
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (isEditingDraft && editingDraftContent && textareaRef.current) {
      textareaRef.current.focus();
      timeoutId = setTimeout(() => {
        adjustTextareaHeight();
      }, 0);
    }
    prevEditingDraftRef.current = isEditingDraft;
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [editingDraftContent, isEditingDraft, adjustTextareaHeight]);

  const handleSend = async () => {
    if (message.trim() && !disabled) {
      if (isEditingDraft && onUpdateDraft) {
        onUpdateDraft(message.trim());
      } else {
        await onSend(message.trim());
      }
      setMessage('');
      clearAutoSave();

      if (textareaRef.current) {
        textareaRef.current.style.height = '80px';
      }
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setMessage(newValue);
    onChange?.(newValue);
    adjustTextareaHeight();
    triggerAutoSave(newValue);
  };

  return (
    <div className="border-border bg-card border-t p-4">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-2">
        {onSubjectChange && (
          <div className="mb-3">
            <SubjectField
              subject={subject}
              onSubjectChange={onSubjectChange}
              placeholder="Enter email subject..."
              className="-mx-2"
            />
          </div>
        )}

        <div className="relative flex-1">
          <Textarea
            ref={textareaRef}
            value={message}
            onChange={handleInput}
            placeholder={placeholder}
            disabled={disabled}
            className={`resize-none pr-10 transition-all duration-200 ${
              isExpanded ? 'min-h-[400px]' : 'max-h-[150px] min-h-[80px]'
            }`}
            rows={3}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-2xs"
            onClick={() => setIsExpanded(!isExpanded)}
            className="absolute top-1 right-1"
            disabled={disabled}
          >
            {isExpanded ? (
              <Minimize2 className="h-3 w-3" />
            ) : (
              <Maximize2 className="h-3 w-3" />
            )}
            <span className="sr-only">
              {isExpanded ? 'Collapse' : 'Expand'} message input
            </span>
          </Button>
          {isAutoSaving && onSaveDraft && (
            <div className="text-muted-foreground absolute right-1 bottom-1 flex items-center gap-1 text-xs">
              <div className="bg-muted-foreground h-1 w-1 animate-pulse rounded-full" />
              Auto-saving draft...
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onAttach && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onAttach}
              disabled={disabled}
              className="shrink-0"
            >
              <Paperclip className="mr-2 h-4 w-4" />
              Attach file
            </Button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <ChatInputActions
              isEditingDraft={isEditingDraft}
              onCancelEdit={onCancelEdit}
              onSend={handleSend}
              disabled={disabled}
              hasContent={!!message.trim()}
              isSending={isSending}
              isAutoSaving={isAutoSaving}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
